import { Dispatch, SetStateAction, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import classNames from "classnames";

import Button from "../../../components/Button";
import { Text } from "../../../components/Type";
import styles from "./index.module.scss";
import NotificationBanner from "../../../components/NotificationBanner";
import { AlertCircle } from "../../../components/Icons/general-interface-2";
import { AdpForm } from "../../../utils/adp-form/adpForm"
import { sanitizeUpdateClientFormData } from "../../../utils/forms/sanitization";
import { ClientType } from "../../../types/clientType";

const IMPORT_XML_DOCUMENT = gql`
  mutation ImportXmlDocument($input: ImportXmlDocumentInput) {
    importXmlDocument(input: $input) {
      message
    }
  }
`;

type PropsType = {
  fileStatus: string | undefined,
  fileId: string | undefined,
  setUploadXmlModalOpen: Dispatch<SetStateAction<boolean>>;
  setSuccessModalOpen: Dispatch<SetStateAction<boolean>>;
  client?: ClientType;
  formId: string | undefined,
};

const UploadXmlModal = ({fileStatus, fileId, setUploadXmlModalOpen, setSuccessModalOpen, client, formId}: PropsType) => {
  const [fileName, setFileName] = useState(undefined);
  const [fileData, setFileData] = useState<string | ArrayBuffer | null | undefined>();
  const [isDisabled, setDisabled] = useState(false);
  const [error, setError] = useState<unknown>();

  const [updateForm] = useMutation(IMPORT_XML_DOCUMENT, {
    onCompleted: () => {
      setDisabled(false);
      setSuccessModalOpen(true);
      setUploadXmlModalOpen(false);
    },
    onError: () => {
      setDisabled(false);
      setError("Error while updating form");
      return null;
    },
  });

  const handleImport = () => {
    setDisabled(true);
    setError(undefined);
    try {
      const adpForm = new AdpForm();
      const form = adpForm.loadFormXml(fileData);
      if (!form) {
        setError("The file must be XML format");
      } else {
        updateForm({
          variables: {
            input: {
              clientId: client?.id,
              clientFileId: fileId,
              clientFormId: formId,
              formData: sanitizeUpdateClientFormData(form),
            }
          }
        });
      }
    } catch (err) {
      setError("Error while updating form");
    }
  };

  const handleClose = () => {
    setUploadXmlModalOpen(false);
  };

  const handleChange = (event) => {
    setDisabled(false);
    setFileName(undefined);
    setError(undefined);
    if (event.target.files?.length > 0 ) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event?.target?.result;
        setFileData(text);
      };
      reader.readAsText(event.target.files[0]);
      setFileName(event.target.files?.item(0).name);
      setError(event.target.files?.item(0).type === "text/xml" ? undefined : "The file must be XML format");
    }
  }

  return (
    <>
      { fileStatus !== "NotStarted" &&
        <NotificationBanner
          warning
          title="The clinical care team has already started the ADP application for this client."
          content="Uploading an XML file will override their inputs."
          className={styles.warningBanner}
        />
      }
      <Text>
      To upload an application that you have started, export the ADP application as an XML file and upload this here. Also, please upload a copy of the PDF of this file, you can go to the client&#39;s profile, select &#39;Upload/view documents&#39; and upload the file under &#39;Other Supporting Documents&#39;. Alternatively, you can provide a hard copy of the application to your Motion Representative.
      </Text>
      <br />
      <div className={classNames(styles.modalGrid5050, styles.bottomBorder)}>
        <Text className={styles.tableHeading} size="medium" weight="bold">
          DOCUMENT
        </Text>
        <Text className={styles.tableHeading} size="medium" weight="bold">
          FILE
        </Text>
      </div>
      <div className={classNames(styles.modalGrid5050, styles.tableItems)}>
        <div>
          <Text size="medium" weight="bold">
            XML File
          </Text>
          <Text size="medium">
            XML File of an ADP form
          </Text>
        </div>
        <div>
          <label htmlFor="xml-upload">
            <Text size="medium" weight="bold" className={styles.uploadText}>
              { fileName || "Select File" }
            </Text>
            <input
              type="file"
              className={styles.hiddenInput}
              id="xml-upload"
              onChange={handleChange}
              accept=".xml"
              disabled={fileName}
            />
          </label>
          { error &&
              <span className={styles.errorText}>
                <AlertCircle className={classNames(styles.icon)}/>
                  {error}
              </span>
          }
        </div>
        {error &&
          <div>
            <label htmlFor="xml-upload-another">
              <Text size="medium" weight="bold" className={styles.uploadText}>
                Upload another file
              </Text>
              <input
                type="file"
                className={styles.hiddenInput}
                id="xml-upload-another"
                onChange={handleChange}
                accept=".xml"
              />
            </label>
          </div>
        }
      </div>
      <div className={styles.horizontalLine100} />
      <div className={styles.modalButton}>
        { fileName && !error ?
          <Button
            primary
            onClick={(handleImport)}
            disabled={isDisabled}
          >
            Import XML
          </Button>
          :
          <Button
            primary
            onClick={(handleClose)}
          >
            Close
          </Button>
        }
      </div>
    </>
  );
}

export default UploadXmlModal;
